import { right } from 'fp-ts/Either';

import { WidgetType } from 'domain/entity/widget';
import { LayoutType } from 'domain/entity/layout';
import DashboardSettings, { ConstraintsPerLayout, WidgetTypeSettings } from 'domain/entity/settings';
import ReadSettingsClient from 'use-case/settings/read/readSettingsClient';

const staticIndicatorConstraints = {
    layouts: [
        {
            type: LayoutType.DESKTOP,
            min_width: 2,
            min_height: 2,
            max_width: 12,
            max_height: 12,
        },
        {
            type: LayoutType.TABLET,
            min_width: 2,
            min_height: 2,
            max_width: 6,
            max_height: 6,
        },
        {
            type: LayoutType.MOBILE,
            min_width: 2,
            min_height: 3,
            max_width: 4,
            max_height: 4,
        },
    ],
};

const staticSmallWidgets = {
    layouts: [
        {
            type: LayoutType.DESKTOP,
            min_width: 1,
            min_height: 1,
            max_width: 12,
            max_height: 12,
        },
        {
            type: LayoutType.TABLET,
            min_width: 1,
            min_height: 1,
            max_width: 6,
            max_height: 6,
        },
        {
            type: LayoutType.MOBILE,
            min_width: 1,
            min_height: 1,
            max_width: 4,
            max_height: 6,
        },
    ],
};

const staticBigChartsConstraints = {
    layouts: [
        {
            type: LayoutType.DESKTOP,
            min_width: 3,
            min_height: 3,
            max_width: 12,
            max_height: 12,
        },
        {
            type: LayoutType.TABLET,
            min_width: 4,
            min_height: 3,
            max_width: 6,
            max_height: 6,
        },
        {
            type: LayoutType.MOBILE,
            min_width: 4,
            min_height: 3,
            max_width: 4,
            max_height: 6,
        },
    ],
};

const staticSmallChartsConstraints = {
    layouts: [
        {
            type: LayoutType.DESKTOP,
            min_width: 3,
            min_height: 3,
            max_width: 12,
            max_height: 12,
        },
        {
            type: LayoutType.TABLET,
            min_width: 3,
            min_height: 3,
            max_width: 6,
            max_height: 6,
        },
        {
            type: LayoutType.MOBILE,
            min_width: 2,
            min_height: 3,
            max_width: 4,
            max_height: 6,
        },
    ],
};

const staticValue = {
    widget_types: [
        {
            type: WidgetType.SINGLE_KPI,
            constraints: staticIndicatorConstraints,
        },
        {
            type: WidgetType.GAUGE,
            constraints: staticIndicatorConstraints,
        },
        {
            type: WidgetType.PERCENTAGE,
            constraints: staticIndicatorConstraints,
        },
        {
            type: WidgetType.SINGLE_BAR,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.SINGLE_LINE,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.STACKED_BAR,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.BAR,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.LINE,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.PIE,
            constraints: staticSmallChartsConstraints,
        },
        {
            type: WidgetType.HEATMAP,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.TABLE,
            constraints: staticBigChartsConstraints,
        },
        {
            type: WidgetType.TEXT,
            constraints: staticSmallWidgets,
        },
        {
            type: WidgetType.IMAGE,
            constraints: staticSmallWidgets,
        },
    ],
};

export default (): ReadSettingsClient => () => new Promise((resolve) => resolve(right(asSettings(staticValue))));

const asSettings = (settingsJson: SettingsJson): DashboardSettings =>
    new DashboardSettings(settingsJson.widget_types.map(asWidgetTypeSettings));

const asWidgetTypeSettings = (widgetType: WidgetTypeSettingsJson): WidgetTypeSettings => ({
    type: widgetType.type as WidgetType,
    constraints: { layouts: widgetType.constraints.layouts.map(asConstraintsPerLayout) },
});

const asConstraintsPerLayout = (constraintsPerLayout: ConstraintsPerLayoutJson): ConstraintsPerLayout => ({
    type: constraintsPerLayout.type as LayoutType,
    minimumWidth: constraintsPerLayout.min_width,
    minimumHeight: constraintsPerLayout.min_height,
    maximumWidth: constraintsPerLayout.max_width,
    maximumHeight: constraintsPerLayout.max_height,
});

type ConstraintsPerLayoutJson = {
    type: string;
    /* eslint-disable-next-line camelcase */
    min_width: number;
    /* eslint-disable-next-line camelcase */
    min_height: number;
    /* eslint-disable-next-line camelcase */
    max_width: number;
    /* eslint-disable-next-line camelcase */
    max_height: number;
};

type WidgetTypeSettingsJson = {
    type: string;
    constraints: {
        layouts: ConstraintsPerLayoutJson[];
    };
};
type SettingsJson = {
    /* eslint-disable-next-line camelcase */
    widget_types: WidgetTypeSettingsJson[];
};
